import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Spinner } from '@heroui/react'
import { useFunnelDetail } from '../../hooks/use-funnel-detail'
import { useFunnelSteps } from '../../hooks/use-funnel-steps'
import { useFunnelVersions } from '../../hooks/use-funnel-versions'
import { funnelApi } from '../../api'
import { getValidationStatus } from '../../lib/validation'
import { FunnelDetailHeader } from './FunnelDetailHeader'
import { FunnelTabs } from './FunnelTabs'
import { FunnelSettings } from '../editor/FunnelSettings'
import { StepList } from '../editor/StepList'
import { EditStepModal } from '../editor/EditStepModal'
import { VersionHistory } from '../editor/VersionHistory'
import { RuleList } from '../rules/RuleList'
import { ConfirmModal } from '../shared/ConfirmModal'
import { CollapsibleSection } from '../shared/CollapsibleSection'
import { OfferPagesEditor } from './OfferPagesEditor'
import { FunnelOverview } from '../widgets/FunnelOverview'
import { StepsFlow } from '../widgets/StepsFlow'
import { ValidationStatus } from '../widgets/ValidationStatus'
import { QuickStats } from '../widgets/QuickStats'
import type { FunnelRule, FunnelAnalytics, FunnelStep, CreateStepPayload, StepType, ExperienceType, CreateOfferPayload, UpdateOfferPayload } from '../../types'
import type { OfferEditorValue } from '../editor/OfferEditor'

function mapOfferDraftToPayload(offerDraft: OfferEditorValue, funnelId: string, stepId?: string): CreateOfferPayload {
  return {
    name: offerDraft.name,
    description: offerDraft.description,
    discountType: offerDraft.discountType,
    discountValue: offerDraft.discountValue,
    funnelId,
    stepId,
    productId: offerDraft.productId,
    variantId: offerDraft.variantId,
    headline: offerDraft.headline,
    imageUrl: offerDraft.imageUrl,
    ctaText: offerDraft.ctaText,
    declineText: offerDraft.declineText,
    pricingType: offerDraft.pricingType,
    pricingValue: offerDraft.pricingValue,
    originalPrice: offerDraft.originalPrice,
    shippingType: offerDraft.shippingType,
    shippingValue: offerDraft.shippingValue,
    displayQuantity: offerDraft.displayQuantity,
    limitQuantity: offerDraft.limitQuantity,
    maxQuantity: offerDraft.maxQuantity,
    bannerText: offerDraft.bannerText,
    subscriptionType: offerDraft.subscriptionType,
    subscriptionName: offerDraft.subscriptionName,
    parentOfferId: offerDraft.parentOfferId,
    minStock: offerDraft.minStock,
    fallbackProductId: offerDraft.fallbackProductId,
    isActive: offerDraft.isActive,
  }
}

export function FunnelDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    funnel,
    steps,
    loading,
    error,
    saveError,
    saving,
    activeTab,
    setActiveTab,
    setSteps,
    updateFunnelLocal,
    saveFunnel,
    deleteFunnel,
    refetch,
  } = useFunnelDetail(id)

  const { createStep, updateStep, deleteStep, reorderSteps, savingStepIds } = useFunnelSteps(id, steps, setSteps, refetch)
  const { versions, loading: versionsLoading } = useFunnelVersions(id)

  // Step modal state
  const [isStepModalOpen, setIsStepModalOpen] = useState(false)
  const [editingStep, setEditingStep] = useState<FunnelStep | null>(null)

  // Delete confirmation state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  // Per-funnel analytics from API
  const [analytics, setAnalytics] = useState<FunnelAnalytics | null>(null)

  const funnelId = funnel?.id
  useEffect(() => {
    if (!funnelId) return
    funnelApi.getFunnelAnalytics(funnelId)
      .then((res) => setAnalytics(res.data))
      .catch(() => {})
  }, [funnelId])

  // Parse rules from funnel data
  const initialRules: FunnelRule[] = Array.isArray(funnel?.rules) ? funnel.rules : []
  const [rulesCount, setRulesCount] = useState(initialRules.length)

  const handleRulesChange = useCallback((rules: FunnelRule[]) => {
    setRulesCount(rules.length)
    updateFunnelLocal({ rules })
  }, [updateFunnelLocal])

  const handleOpenAddStep = useCallback(() => {
    setEditingStep(null)
    setIsStepModalOpen(true)
  }, [])

  const handleOpenEditStep = useCallback((stepId: string) => {
    const step = steps.find((s) => s.id === stepId)
    if (step) {
      setEditingStep(step)
      setIsStepModalOpen(true)
    }
  }, [steps])

  const handleStepSave = useCallback(async (data: CreateStepPayload, offerDraft?: OfferEditorValue) => {
    let nextOfferId = data.offerId || editingStep?.offerId

    if (id && offerDraft) {
      const stepIdForOffer = editingStep?.id
      const payload = mapOfferDraftToPayload(offerDraft, id, stepIdForOffer)

      if (nextOfferId) {
        const updatePayload: UpdateOfferPayload = payload
        await funnelApi.updateOffer(nextOfferId, updatePayload)
      } else {
        const created = await funnelApi.createOffer(payload)
        nextOfferId = created.data.id
      }
    }

    if (editingStep) {
      await updateStep(editingStep.id, {
        name: data.name,
        stepType: data.stepType as StepType,
        position: data.position,
        offerId: nextOfferId,
        nextStepAccept: data.nextStepAccept,
        nextStepDecline: data.nextStepDecline,
        experienceType: data.experienceType as ExperienceType | undefined,
      })
    } else {
      await createStep({
        ...data,
        offerId: nextOfferId,
      })
    }
  }, [editingStep, createStep, id, updateStep])

  const handleDeleteClick = useCallback(() => {
    setIsDeleteModalOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    await deleteFunnel()
    navigate('/funnels')
  }, [deleteFunnel, navigate])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !funnel) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-lg font-medium text-red-600">{error || 'Funnel not found'}</p>
          <button
            className="mt-4 text-sm text-blue-600 hover:underline"
            onClick={() => navigate('/funnels')}
          >
            Back to funnels
          </button>
        </div>
      </div>
    )
  }

  const validationStatus = getValidationStatus(steps.length, rulesCount > 0)

  return (
    <div className="funnels-theme min-h-screen animate-fade-in bg-fn-page">
      <FunnelDetailHeader funnel={funnel} saving={saving} onSave={saveFunnel} onDelete={handleDeleteClick} />
      {saveError && (
        <div className="mx-6 mt-2 px-4 py-2 rounded-lg bg-danger-50 border border-danger-200 text-danger text-sm">
          {saveError}
        </div>
      )}
      <FunnelTabs activeTab={activeTab} onChange={setActiveTab} />

      <div className="flex gap-6 p-6">
        <div className="flex-1 space-y-6">
          {activeTab === 'logic' ? (
            <>
              <FunnelSettings funnel={funnel} onChange={updateFunnelLocal} />
              <StepList
                steps={steps}
                onReorder={reorderSteps}
                onEdit={handleOpenEditStep}
                onDelete={deleteStep}
                onBuildPage={(stepId) => navigate(`/funnels/${id}/steps/${stepId}/builder`)}
                onAddStep={handleOpenAddStep}
                savingStepIds={savingStepIds}
              />
              <CollapsibleSection
                title="Targeting Rules"
                defaultOpen={initialRules.length > 0}
                badge={
                  rulesCount > 0 ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {rulesCount} {rulesCount === 1 ? 'rule' : 'rules'}
                    </span>
                  ) : undefined
                }
              >
                <RuleList funnelId={id} initialRules={initialRules} onRulesChange={handleRulesChange} />
              </CollapsibleSection>
              <VersionHistory versions={versions} loading={versionsLoading} />
            </>
          ) : (
            <OfferPagesEditor funnelId={id!} steps={steps} onAddStep={handleOpenAddStep} />
          )}
        </div>

        <div className="w-96 space-y-4">
          <FunnelOverview funnel={funnel} />
          <StepsFlow steps={steps} />
          <ValidationStatus status={validationStatus} />
          {analytics && <QuickStats analytics={analytics} />}
        </div>
      </div>

      <EditStepModal
        isOpen={isStepModalOpen}
        onClose={() => { setIsStepModalOpen(false); setEditingStep(null) }}
        onSave={handleStepSave}
        initialData={editingStep ?? undefined}
        stepsCount={steps.length}
        allSteps={steps}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Funnel"
        description={`Are you sure you want to delete "${funnel.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  )
}
